import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { checkForNewVersion, forceAppUpdate, notifyIfJustUpdated } from "@/lib/appUpdate";
import { APP_VERSION } from "@/version";
import { HardHat, LogIn, Eye, EyeOff, Wrench, Zap, Hammer, Ruler, Building2, Cog, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { LangSwitch } from "@/components/LangSwitch";
import { GooglePlayBadge } from "@/components/GooglePlayBadge";

// Iter 30y: hero "blueprint multi-utility" — composizione CSS/SVG senza foto stock.
// Multi-lingua ready, vettoriale, leggera, scalabile per app store.
function HeroBackground() {
  return (
    <>
      {/* Sfondo dark con gradient industriale */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-[#0a1838] to-slate-950" />
      {/* Pattern blueprint griglia */}
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      {/* Bagliore brand sull'angolo */}
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[#0044FF] blur-3xl opacity-30" />
      <div className="absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-[#FF6A00] blur-3xl opacity-15" />

      {/* Linee diagonali "tracciati" stile blueprint */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.12]"
        viewBox="0 0 800 600"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <pattern id="dotpattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="#7DA8FF" />
          </pattern>
        </defs>
        <rect width="800" height="600" fill="url(#dotpattern)" />
        <line x1="0" y1="500" x2="800" y2="100" stroke="#7DA8FF" strokeWidth="0.7" strokeDasharray="6 6" />
        <line x1="100" y1="600" x2="700" y2="0" stroke="#7DA8FF" strokeWidth="0.7" strokeDasharray="6 6" />
        {/* Schema gru stilizzata */}
        <g stroke="#7DA8FF" strokeWidth="1.5" fill="none" opacity="0.9">
          <line x1="120" y1="500" x2="120" y2="160" />
          <line x1="100" y1="500" x2="140" y2="500" />
          <line x1="120" y1="180" x2="320" y2="180" />
          <line x1="120" y1="180" x2="40" y2="220" />
          <line x1="200" y1="180" x2="200" y2="230" />
          <line x1="180" y1="230" x2="220" y2="230" />
        </g>
      </svg>

      {/* Glyph icone "trades" — sparse, semi-trasparenti */}
      <div className="absolute inset-0 pointer-events-none">
        {[
          { Icon: HardHat, top: "12%", left: "62%", size: 36, rot: -8 },
          { Icon: Wrench, top: "32%", left: "78%", size: 28, rot: 14 },
          { Icon: Hammer, top: "62%", left: "70%", size: 32, rot: -22 },
          { Icon: Zap, top: "78%", left: "58%", size: 26, rot: 6 },
          { Icon: Ruler, top: "22%", left: "44%", size: 24, rot: 18 },
          { Icon: Building2, top: "70%", left: "20%", size: 30, rot: -4 },
          { Icon: Cog, top: "44%", left: "30%", size: 26, rot: 24 },
        ].map(({ Icon, top, left, size, rot }, i) => (
          <Icon
            key={i}
            style={{
              position: "absolute",
              top,
              left,
              width: size,
              height: size,
              transform: `rotate(${rot}deg)`,
              color: "#7DA8FF",
              opacity: 0.18,
            }}
            strokeWidth={1.5}
          />
        ))}
      </div>
    </>
  );
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  // Iter 31hh: se sul server c'è una versione più nuova di quella in cache,
  // mostriamo il banner "Aggiorna" PRIMA del login per evitare errori
  // (es. "account non trovato") causati dal bundle vecchio.
  const [newVersion, setNewVersion] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    // Iter 45: conferma post-aggiornamento + auto-update anche dalla login
    const justV = notifyIfJustUpdated();
    if (justV) {
      toast.success(`✔ App aggiornata: stai usando l'ultima versione ${justV}`, {
        duration: 6000,
      });
    }
    checkForNewVersion().then((v) => {
      if (!v) return;
      setNewVersion(v);
      try {
        if (sessionStorage.getItem("auto_update_v") === v) return;
        sessionStorage.setItem("auto_update_v", v);
      } catch { /* ignore */ }
      toast.info(`Nuova versione ${v}: aggiornamento automatico in corso…`, {
        duration: 3000,
      });
      setTimeout(() => forceAppUpdate(), 1200);
    });
  }, []);

  const handleUpdate = async () => {
    setUpdating(true);
    await forceAppUpdate();
  };

  // Iter 136: ?next=/da-vidimare (link nelle email di notifica) → dopo il login si va lì.
  // Letto UNA volta al mount: l'ultimo render post-login avviene con l'URL già cambiato.
  const [nextPath] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("next") || "";
    return /^\/[a-zA-Z0-9\-_/?=&%]*$/.test(p) ? p : "/home";
  });

  if (user) return <Navigate to={nextPath} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const em = email.trim();
    if (!em) return setError(t("emailRequired"));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return setError(t("emailInvalid"));
    if (!password) return setError(t("pwdRequired"));
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Iter 60: il bottone demo è visibile SOLO tramite link dedicato (/demo o
  // ?demo=1) — condiviso con giudici/prospect, mai visto dai dipendenti.
  const showDemo =
    window.location.pathname === "/demo" ||
    new URLSearchParams(window.location.search).get("demo") === "1";
  // Iter 135: su un dispositivo dove è entrata l'azienda proprietaria non mostriamo "Prova gratis 14 giorni" / "Prova la demo"
  const ownerDevice = localStorage.getItem("il_owner_device") === "1";

  // Iter 59: accesso rapido all'account demo (dati d'esempio isolati)
  const handleDemo = async () => {
    setError("");
    setLoading(true);
    try {
      await login("demo@il-report.it", "demo2026");
      navigate("/home", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Hero / branding */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-md bg-[#0044FF] grid place-items-center shadow-lg shadow-[#0044FF]/40">
              <HardHat className="h-7 w-7" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-black text-2xl tracking-tight">
                IL-Report
              </div>
              <div className="text-sm text-slate-300">
                Field-work hours for any trade
              </div>
            </div>
          </div>

          <div className="max-w-lg">
            <h1 className="font-heading font-black text-5xl xl:text-6xl tracking-tight leading-[1.05]">
              {t("heroA")}
              <br />
              <span className="text-[#7DA8FF]">{t("heroB")}</span>
            </h1>
            {/* Iter 50b: testo promozionale rimosso su richiesta — da riusare
                nella scheda Google Play / App Store quando l'app andrà in
                vendita: "Per carpentieri, saldatori, montatori, elettricisti,
                idraulici e manutentori — e per ogni azienda con cantieri sotto
                casa o all'altro capo del mondo. Una sola app per i rapportini
                di cantiere e officina: compila al volo, filtra per commessa,
                esporta nel gestionale, invia i rapportini via email in
                automatico." */}
            <div className="mt-8 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400 font-bold">
              <span className="px-2 py-1 rounded border border-slate-700 bg-slate-900/40">PWA</span>
              <span className="px-2 py-1 rounded border border-slate-700 bg-slate-900/40">Offline-ready</span>
              <span className="px-2 py-1 rounded border border-slate-700 bg-slate-900/40">iOS · Android · Web</span>
              <span className="px-2 py-1 rounded border border-[#0044FF] bg-[#0044FF]/15 text-[#7DA8FF]">AI-assisted</span>
            </div>
          </div>

          <div className="text-xs text-slate-400 font-mono">
            Made by IL-LAB · Crafted for crews on the move
          </div>
        </div>
      </div>

      {/* Login form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-md bg-[#0044FF] text-white grid place-items-center">
              <HardHat className="h-6 w-6" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-extrabold text-lg tracking-tight">
                IL-Report
              </div>
              <div className="text-xs text-slate-500">
                Rapportini interni di lavoro
              </div>
            </div>
          </div>

          <h2 className="font-heading font-extrabold text-3xl tracking-tight flex items-center justify-between gap-3">
            {t("accedi", { ns: "common" })}
            <LangSwitch />
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {t("loginSub")}
          </p>

          {newVersion && (
            <div
              className="mt-5 rounded-md border-2 border-amber-400 bg-amber-50 p-3.5"
              data-testid="stale-version-banner"
            >
              <div className="text-sm font-bold text-amber-900">
                ⚠️ Stai usando una versione vecchia dell&apos;app
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Tu hai la <b>v{APP_VERSION}</b>, è disponibile la{" "}
                <b>v{newVersion}</b>. Aggiorna prima di accedere per evitare
                errori (es. &quot;account non trovato&quot;).
              </div>
              <Button
                type="button"
                onClick={handleUpdate}
                disabled={updating}
                data-testid="stale-version-update-button"
                className="mt-2.5 h-11 w-full bg-amber-600 hover:bg-amber-700 text-white font-bold"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${updating ? "animate-spin" : ""}`} />
                {updating ? "Aggiornamento in corso…" : "Aggiorna l'app adesso"}
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-bold">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@azienda.it"
                required
                autoComplete="email"
                className="h-14 text-base"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-bold">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="h-14 text-base pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  data-testid="toggle-password-visibility"
                  aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                >
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => navigate("/password-dimenticata")}
                  data-testid="login-forgot-password-link"
                  className="text-xs font-bold text-[#0044FF] hover:underline"
                >
                  Password dimenticata?
                </button>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                data-testid="login-error"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {error}
                <button
                  type="button"
                  onClick={handleUpdate}
                  disabled={updating}
                  data-testid="login-error-update-link"
                  className="mt-1.5 flex items-center gap-1.5 text-xs font-bold text-red-800 underline hover:text-red-900"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${updating ? "animate-spin" : ""}`} />
                  Problemi ad accedere? Aggiorna l&apos;app e riprova
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-14 text-base font-bold bg-[#0044FF] hover:bg-[#0033CC]"
            >
              {loading ? t("loginLoading") : (
                <>
                  <LogIn className="h-5 w-5 mr-2" /> {t("accedi", { ns: "common" })}
                </>
              )}
            </Button>
            {showDemo && (
              <button
                type="button"
                onClick={handleDemo}
                disabled={loading}
                data-testid="demo-login-button"
                className="w-full h-12 rounded-md border-2 border-dashed border-amber-400 bg-amber-50 text-amber-800 text-sm font-bold hover:bg-amber-100 transition-colors"
              >
                {t("loginDemo")}
              </button>
            )}
            {!ownerDevice && (
            <button
              type="button"
              onClick={() => navigate("/registrati")}
              data-testid="login-register-link"
              className="w-full h-12 rounded-md border-2 border-[#0044FF]/30 bg-blue-50/50 text-[#0044FF] text-sm font-bold hover:bg-blue-50 transition-colors"
            >
              {t("loginRegister")}
            </button>
            )}
          </form>
          <div className="mt-5 flex justify-center">
            <GooglePlayBadge />
          </div>

          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-400">
            <span data-testid="login-app-version">v{APP_VERSION}</span>
            <span>·</span>
            <button
              type="button"
              onClick={handleUpdate}
              disabled={updating}
              data-testid="login-footer-update-link"
              className="underline hover:text-slate-600 inline-flex items-center gap-1"
            >
              <RefreshCw className={`h-3 w-3 ${updating ? "animate-spin" : ""}`} />
              Aggiorna app
            </button>
            {!showDemo && !ownerDevice && (
              <>
                <span>·</span>
                <button
                  type="button"
                  onClick={handleDemo}
                  disabled={loading}
                  data-testid="login-footer-demo-link"
                  className="underline hover:text-slate-600"
                >
                  Sei un visitatore? Prova la demo
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
